import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Plan } from '../types'

export const MAX_UNITS_PER_CHECKOUT = 20

export interface CartItem {
  productId: string
  slug: string
  name: string
  plan: Plan
  priceCents: number
  currency: string
  quantity: number
}

interface CartContextValue {
  items: CartItem[]
  count: number
  isOpen: boolean
  addItem: (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => void
  setQuantity: (productId: string, plan: Plan, quantity: number) => void
  removeItem: (productId: string, plan: Plan) => void
  clear: () => void
  openCart: () => void
  closeCart: () => void
}

const STORAGE_KEY = 'cheatseclipse.cart.v2'
const CartContext = createContext<CartContextValue | null>(null)

const lineKey = (productId: string, plan: Plan) => `${productId}:${plan}`

function loadStoredItems(): CartItem[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (item): item is CartItem =>
        typeof item === 'object' && item !== null &&
        typeof (item as CartItem).productId === 'string' &&
        typeof (item as CartItem).name === 'string' &&
        typeof (item as CartItem).priceCents === 'number' &&
        typeof (item as CartItem).quantity === 'number' &&
        (item as CartItem).quantity >= 1 &&
        ((item as CartItem).plan === 'weekly' || (item as CartItem).plan === 'monthly'),
    )
  } catch {
    return []
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() =>
    typeof window === 'undefined' ? [] : loadStoredItems(),
  )
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    } catch {
      // storage may be unavailable (private mode); the cart just won't persist
    }
  }, [items])

  const addItem = useCallback((item: Omit<CartItem, 'quantity'> & { quantity?: number }) => {
    const addUnits = Math.max(1, item.quantity ?? 1)
    setItems((current) => {
      const key = lineKey(item.productId, item.plan)
      const existing = current.find((line) => lineKey(line.productId, line.plan) === key)
      if (existing) {
        return current.map((line) =>
          lineKey(line.productId, line.plan) === key
            ? { ...line, priceCents: item.priceCents, quantity: Math.min(MAX_UNITS_PER_CHECKOUT, line.quantity + addUnits) }
            : line,
        )
      }
      return [...current, { ...item, quantity: Math.min(MAX_UNITS_PER_CHECKOUT, addUnits) }]
    })
    setIsOpen(true)
  }, [])

  const setQuantity = useCallback((productId: string, plan: Plan, quantity: number) => {
    setItems((current) => {
      if (quantity < 1) {
        return current.filter((line) => lineKey(line.productId, line.plan) !== lineKey(productId, plan))
      }
      const clamped = Math.min(MAX_UNITS_PER_CHECKOUT, Math.floor(quantity))
      return current.map((line) =>
        lineKey(line.productId, line.plan) === lineKey(productId, plan) ? { ...line, quantity: clamped } : line,
      )
    })
  }, [])

  const removeItem = useCallback((productId: string, plan: Plan) => {
    setItems((current) => current.filter((line) => lineKey(line.productId, line.plan) !== lineKey(productId, plan)))
  }, [])

  const clear = useCallback(() => setItems([]), [])
  const openCart = useCallback(() => setIsOpen(true), [])
  const closeCart = useCallback(() => setIsOpen(false), [])

  const value = useMemo(
    () => ({
      items,
      count: items.reduce((sum, line) => sum + line.quantity, 0),
      isOpen,
      addItem,
      setQuantity,
      removeItem,
      clear,
      openCart,
      closeCart,
    }),
    [items, isOpen, addItem, setQuantity, removeItem, clear, openCart, closeCart],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
  const context = useContext(CartContext)
  if (!context) throw new Error('useCart must be used inside <CartProvider>')
  return context
}
